import { Building2, ChevronsUpDown, Plus } from 'lucide-react';
import { AppShell } from '@/components/shell/AppShell';
import { Menu, Skeleton, type MenuHeading, type MenuItem } from '@/components/ui';
import { AuthGuard } from '@/lib/auth/AuthGuard';
import { ownerCommands, ownerNav } from '@/lib/nav/owner';
import { ALL_PROPERTIES, PropertyProvider, useProperty } from './property-context';

function PropertySwitcher() {
  const { properties, isLoading, error, selected, selectedId, setSelectedId, refetch } = useProperty();

  if (isLoading) {
    return <Skeleton className="h-9 w-48" />;
  }
  if (error) {
    return (
      <button type="button" onClick={refetch} className="rounded-lg px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50">
        Couldn’t load properties · Retry
      </button>
    );
  }

  const items: MenuItem[] = [
    { label: 'All properties', description: `${properties.length} ${properties.length === 1 ? 'property' : 'properties'}`, checked: selectedId === ALL_PROPERTIES, onSelect: () => setSelectedId(ALL_PROPERTIES) },
    ...properties.map<MenuItem>((property) => ({
      label: property.name,
      description: [property.city, property.code].filter(Boolean).join(' · '),
      checked: selectedId === property.id,
      onSelect: () => setSelectedId(property.id),
    })),
  ];

  return (
    <Menu
      label="Switch property"
      align="start"
      items={items}
      triggerClassName="flex h-9 max-w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-800 shadow-card transition-colors hover:border-slate-300"
      trigger={
        <>
          <Building2 className="size-4 shrink-0 text-amethyst-700" aria-hidden />
          <span className="truncate">{selected?.name ?? 'All properties'}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-slate-400" aria-hidden />
        </>
      }
    />
  );
}

const newMenuItems = (): (MenuItem | MenuHeading)[] => {
  const byId = (id: string) => ownerCommands.find((command) => command.id === id);
  const entry = (id: string): MenuItem[] => {
    const command = byId(id);
    return command ? [{ label: command.label, icon: command.icon, to: command.to }] : [];
  };
  return [
    { heading: 'People' },
    ...entry('new-resident'),
    ...entry('new-lead'),
    { heading: 'Money' },
    ...entry('collect'),
    ...entry('new-due'),
    ...entry('new-expense'),
    { heading: 'Property' },
    ...entry('new-issue'),
    ...entry('new-announcement'),
  ];
};

/** One labelled entry point for creating anything. No floating buttons. */
function NewMenu() {
  return (
    <Menu
      label="Create new"
      items={newMenuItems()}
      triggerClassName="inline-flex h-9 items-center gap-1.5 rounded-lg bg-amethyst-700 px-3.5 text-sm font-semibold text-white shadow-card transition-colors hover:bg-amethyst-800"
      trigger={
        <>
          <Plus className="size-4" aria-hidden />
          New
        </>
      }
    />
  );
}

export function OwnerLayout() {
  return (
    <AuthGuard audience="owner">
      <PropertyProvider>
        <AppShell audience="owner" nav={ownerNav} commands={ownerCommands} topbarLeading={<PropertySwitcher />} topbarTrailing={<NewMenu />} />
      </PropertyProvider>
    </AuthGuard>
  );
}
